import { isNil } from 'lodash';
import { MBStorage } from '.';

const TTL_MAP_KEY = 'ttl-value-map';

interface TimeToLiveRecord {
  expiresAt: number;
}

interface TimeToLiveMap {
  [key: string]: TimeToLiveRecord;
}

export namespace JsonStorage {
  /**
   * Set a value to @MBStorage using a time to live caching strategy
   *
   * @param key the storage key associated with the data
   * @param value the value to store
   * @param timeToLiveMs the time for the value to be valid in the storage
   * @returns A promise that resolves on success and rejects on failure
   */
  export async function set<T>(key: string, value: T, timeToLiveMs?: number): Promise<void> {
    await MBStorage.setJSON<T>(key, value);
    if (isNil(timeToLiveMs)) {
      return;
    }
    const timeNow = Date.now();
    const timeToLiveMap: TimeToLiveMap = await getTimeToLiveMap();
    const newTimeToLiveMap = {
      ...timeToLiveMap,
      [key]: { expiresAt: timeNow + timeToLiveMs },
    };
    await MBStorage.setJSON<TimeToLiveMap>(TTL_MAP_KEY, newTimeToLiveMap);
  }

  /**
   * Get a value from @MBStorage that was previously set using @setStorageValueUsingTimeToLiveStorage
   *
   * @param key the storage key associated with the data
   * @returns A promise that resolves with the stored data if it exists within the time to live,
   * undefined if it does not exist in the time to live. The promise rejects if there was an error
   * accessing the storage.
   */
  export async function get<T>(key: string): Promise<T | undefined> {
    const timeToLiveMap: TimeToLiveMap = await getTimeToLiveMap();
    const timeToLiveRecord = timeToLiveMap[key];
    const isExpired = isRecordExpired(timeToLiveRecord);
    if (isExpired) {
      await remove(key);
      return undefined;
    } else {
      return await MBStorage.getJSON<T>(key);
    }
  }

  /**
   * Clear a value from the time to live storage
   *
   * @param key the storage key associated with the data
   * @returns A promise that resolves on success and rejects on failure
   */
  export async function remove(key: string): Promise<void> {
    const timeToLiveMap: TimeToLiveMap = await getTimeToLiveMap();
    delete timeToLiveMap[key];
    await MBStorage.setJSON<TimeToLiveMap>(TTL_MAP_KEY, timeToLiveMap);
    await MBStorage.remove(key);
  }

  /**
   * Cleanup all stale vales from the time to live storage
   *
   * @returns A promise that resolves on success and rejects on failure
   */
  export async function cleanup(): Promise<void> {
    await removeExpiredKeys();
    await removeUnusedTTLKeys();
  }

  export async function removeExpiredKeys(): Promise<void> {
    const timeToLiveMap: TimeToLiveMap = await getTimeToLiveMap();
    Object.keys(timeToLiveMap)
      .filter(key => isRecordExpired(timeToLiveMap[key]))
      .forEach(expiredKey => {
        delete timeToLiveMap[expiredKey];
        MBStorage.remove(expiredKey);
      });

    return MBStorage.setJSON<TimeToLiveMap>(TTL_MAP_KEY, timeToLiveMap);
  }

  export async function removeUnusedTTLKeys(): Promise<void> {
    const keys = await MBStorage.keys();
    const timeToLiveMap: TimeToLiveMap = await getTimeToLiveMap();
    Object.keys(timeToLiveMap)
      .filter(key => !keys.includes(key))
      .forEach(unusedKey => {
        delete timeToLiveMap[unusedKey];
      });

    return MBStorage.setJSON<TimeToLiveMap>(TTL_MAP_KEY, timeToLiveMap);
  }
}

async function getTimeToLiveMap(): Promise<TimeToLiveMap> {
  const timeToLiveMap = await MBStorage.getJSON<TimeToLiveMap>(TTL_MAP_KEY);
  return timeToLiveMap ?? {};
}

function isRecordExpired(record?: TimeToLiveRecord): boolean {
  if (isNil(record?.expiresAt)) {
    return false;
  }
  const timeNow = Date.now();
  return timeNow > record.expiresAt;
}
